import { useCallback, useRef, useState } from 'react';
import { mediaUrl, type MediaId, type MediaVariantName } from 'models/Media';
import useMedia from 'networking/mindfeed/hooks/useMedia';

type MediaStatus = 'loading' | 'ready' | 'error';

// Gives you the URL to put in an <img>, <video> or <audio> for a media file.
//
// - `url` is the smaller copy you asked for, or the full file if there isn't one.
// - `originalUrl` is always the full file.
// - Pass `onError` to the element. If the file fails to load (for example the
//   link has expired), we get a new link and try once more.
export const useMediaUrl = (
  mediaId?: MediaId,
  variant?: MediaVariantName,
): {
  url: string | null;
  originalUrl: string | null;
  status: MediaStatus;
  onError: () => void;
} => {
  const { data: asset, isError, refetch } = useMedia(mediaId);
  // We track which file we retried and which one failed, so showing a
  // different file starts fresh.
  const retriedId = useRef<MediaId>(undefined);
  const [failedId, setFailedId] = useState<MediaId>();

  const onError = useCallback(() => {
    if (!mediaId) return;
    if (retriedId.current === mediaId) {
      setFailedId(mediaId);
      return;
    }
    retriedId.current = mediaId;
    refetch();
  }, [mediaId, refetch]);

  const url = asset ? mediaUrl(asset, variant) : null;
  const failed = isError || (!!mediaId && failedId === mediaId);
  const status: MediaStatus = failed ? 'error' : url ? 'ready' : 'loading';

  return { url, originalUrl: asset?.urls.original ?? null, status, onError };
};
